import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';

function PhaseNode({ phase, index, total }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      className="relative flex-1 min-w-0"
      data-testid={`roadmap-phase-${index}`}
    >
      {/* Connector line */}
      {index < total - 1 && (
        <div className="hidden md:block absolute top-[1.375rem] left-[calc(50%+1.5rem)] right-0 h-px overflow-hidden z-0">
          <div className="h-full bg-gradient-to-r from-[#3D5638]/50 to-[#3D5638]/15" />
          {inView && (
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, transparent 30%, rgba(189,214,181,0.65) 50%, transparent 70%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: 'light-trace 4s ease-in-out infinite',
              }}
            />
          )}
        </div>
      )}

      {/* Node dot */}
      <div className="flex justify-center mb-5 relative z-10">
        <div
          className={`w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
            phase.active
              ? 'bg-[#3D5638] border-[#BDD6B5] shadow-[0_0_16px_rgba(189,214,181,0.45)] animate-bio-pulse'
              : 'bg-[#0E110E] border-[#3D5638]/50'
          }`}
        >
          <span className="font-mono text-[10px] text-[#BDD6B5]/80">
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
      </div>

      {/* Card */}
      <div
        className={`glass-card p-5 h-full ${
          phase.active ? 'border-[#BDD6B5]/35 shadow-[0_0_20px_rgba(61,86,56,0.1)]' : ''
        }`}
      >
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="font-mono text-[9px] tracking-widest uppercase text-[#BDD6B5] bg-[#3D5638]/25 px-2 py-0.5 rounded-full">
            {phase.stage}
          </span>
          {phase.active && (
            <span className="text-[9px] font-mono uppercase tracking-widest bg-[#3D5638]/25 text-[#BDD6B5] px-2 py-0.5 rounded-full border border-[#BDD6B5]/30">
              Active
            </span>
          )}
        </div>
        <p className="font-mono text-[10px] tracking-widest uppercase text-white/30 mb-1">
          {phase.phase}
        </p>
        <h3 className="text-base font-semibold text-white mb-1">{phase.title}</h3>
        <p className="text-xs font-mono text-[#A4C3D2]/50 mb-3">{phase.date}</p>
        <p className="text-xs text-white/72 leading-relaxed">{phase.desc}</p>
      </div>
    </motion.div>
  );
}

export default function RoadmapSection() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const phases = t('roadmap.phases');

  return (
    <section id="roadmap" className="relative py-24 sm:py-32 px-6 sm:px-12 lg:px-24" data-testid="roadmap-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-14"
        >
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium">
            {t('roadmap.heading')}{' '}
            <span className="text-[#BDD6B5]">{t('roadmap.headingGreen')}</span>
          </h2>
          {t('roadmap.subtitle') && (
            <p className="mt-4 text-sm text-white/45 max-w-2xl leading-relaxed font-mono tracking-wide">
              {t('roadmap.subtitle')}
            </p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-3">
          {Array.isArray(phases) && phases.map((p, i) => (
            <PhaseNode key={i} phase={p} index={i} total={phases.length} />
          ))}
        </div>
      </div>
    </section>
  );
}
